import {
    CognitoUserPool,
    CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { gql, Resolver } from '@apollo/client';
import _get from 'lodash/get';

import { GET_SESSION_IS_SIGNING_UP } from './getSessionIsSigningUp';

export interface Args {
    name: string;
    username: string;
    password: string;
    clientId: string;
    userPoolId: string;
    statementId?: string;
}

export const signUpWithCognito: Resolver = async (
    obj,
    args: Args,
    { cache },
): Promise<SignUpResult | WrappedCognitoError> => {
    const {
        name,
        username: usernameArg,
        password,
        clientId,
        userPoolId,
        statementId,
    } = args;
    const username = usernameArg.toLowerCase();

    const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: clientId,
    });
    const emailAttribute = new CognitoUserAttribute({
        Name: 'email',
        Value: username,
    });
    const nameAttribute = new CognitoUserAttribute({
        Name: 'name',
        Value: name,
    });
    const attributeList = [emailAttribute, nameAttribute];

    if (statementId) {
        const statementIdAttribute = new CognitoUserAttribute({
            // The custom attribute is titled 'custom:statement_url' for legacy reasons; it's
            // actually the statement_id.
            Name: 'custom:statement_url',
            Value: statementId,
        });

        attributeList.push(statementIdAttribute);
    }

    const createUser = (): Promise<SignUpResult> =>
        new Promise((resolve, reject) => {
            userPool.signUp(
                username,
                password,
                attributeList,
                [],
                (err, result) => {
                    if (err) {
                        reject(err);
                    } else {
                        if (result === undefined) {
                            throw new Error('User not found');
                        }

                        const userSub = _get(result, ['userSub']);

                        if (!userSub) {
                            reject(
                                new Error(
                                    'userSub not found - was the user created?',
                                ),
                            );
                        }

                        cache.writeQuery({
                            query: GET_SESSION_IS_SIGNING_UP,
                            data: {
                                session: {
                                    isSigningUp: true,
                                    __typename: 'Session',
                                },
                            },
                        });

                        resolve({ userSub });
                    }
                },
            );
        });

    try {
        return createUser();
    } catch (err) {
        return { error: err };
    }
};

export const SIGN_UP_WITH_COGNITO = gql`
    mutation SignUpWithCognito(
        $name: String
        $username: String
        $password: String
        $clientId: String
        $userPoolId: String
        $statementId: String
    ) {
        signUpWithCognito(
            name: $name
            username: $username
            password: $password
            clientId: $clientId
            userPoolId: $userPoolId
            statementId: $statementId
        ) @client
    }
`;
